<template>
  <div>
    <loading :active="isLoading" background-color="#272727" :opacity="0.2"></loading>

    <section
      :class="{'hide-wrapper': isHide}"
      ref="pdfContent"
    >
      <slot name="pdf-content"/>
    </section>
  </div>
</template>

<script>
import html2Canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import FileSaver from 'file-saver';

export default {
  name: 'HtmlToPdf',
  props: {
    filename: {
      type: String,
      default: `${new Date().getTime()}`
    },
    enableDownload: { // 下載開關
			type: Boolean,
			default: false
		},
		isIframePreviewPdf: { // 是否使用Iframe預覽PDF
			type: Boolean,
			default: true
		},
    isConvertPdf: { // 是否要轉換成pdf
      type: Boolean,
			default: true
    },
    exportZip: { // 導出zip
      type: Boolean,
			default: false
    },
    isHide: { // 是否隱藏
      type: Boolean,
			default: true
    },
    params: {
      type: Object,
      default: () => {
        return {
          format: 'a4',
          orientation: 'p'
        }
      }
    }
  },
  data () {
    return {
      isLoading: false,
      html2CanvasParams: {
        allowTaint: false,
        taintTest: false,
        logging: false,
        useCORS: true,
        dpi: 300, //将分辨率提高到特定的DPI 提高四倍
        scale: 2 //按比例增加分辨率
      },
      paperParams: {
        size: {
          p: {
            a4: {
              width: 210,
              height: 297
            },
            a5: {
              width: 148,
              height: 210
            }
          },
          l: {
            a4: {
              width: 297,
              height: 210
            },
            a5: {
              width: 210,
              height: 148
            }
          }
        },
        margin: [5, 10, 5, 10],
      },
      zipSplitPdfNum: 50
    }
  },
  computed: {
    pdfParams() {
      return {
        ...this.params,
        unit: 'mm',
        putOnlyUsedFonts: true
      }
    }
  },
  mounted() {

  },
  methods: {
    // 生成 pdf
    async generatePdf() {
      console.log(`開始時間：${this.getNowTime()}`);

      this.isLoading = true;

      if (this.exportZip) {
        const parentElement = this.$refs.pdfContent;
        const ArrOfContentChildren = Array.from(parentElement.children);

        const p = await this.getPdf(ArrOfContentChildren);

        this.pdfEventHandler(p);
      } else {
        const parentElement = this.$refs.pdfContent;
        const ArrOfContentChildren = Array.from(parentElement.children);
        const childElementCount = parentElement.childElementCount;
        const num = this.zipSplitPdfNum;
        const batch = childElementCount < num ? 1 : Math.ceil(childElementCount / num);

        let promises = [];

        for (let i = 0 ; i < batch; i++) {
          const slice_end =  childElementCount < num ? childElementCount + 1 : i * num + num;
          const el = ArrOfContentChildren.slice(i * num, slice_end);
          const p = await this.getPdf(el);

          promises.push(p);

          if (i + 1 === batch) {
            this.pdfEventHandler(promises);
          }
        }
      }
    },
    // 取得 pdf
    async getPdf(el) {
      return new Promise(async(resolve, reject) => {
        const pdf = new jsPDF(this.pdfParams);
        let img = '';

        for (const [index, childElement] of el.entries()) {
          const canvas = await html2Canvas(childElement, this.html2CanvasParams);
          const paper_params = this.paperParams;
          const pdf_params = this.pdfParams;

          let ctx = canvas.getContext('2d'),
              pw = paper_params.size[pdf_params.orientation][pdf_params.format].width - paper_params.margin[1] - paper_params.margin[3], // 左右保留10mm的邊距
              ph = paper_params.size[pdf_params.orientation][pdf_params.format].height - paper_params.margin[0] - paper_params.margin[2], // 上下保留5mm的邊距
              imgHeight = Math.floor(ph * canvas.width / pw),    //按A4显示比例换算一页图像的像素高度
              renderedHeight = 0;

          while(renderedHeight < canvas.height) {
            const page = document.createElement("canvas");
            page.width = canvas.width;
            page.height = Math.min(imgHeight, canvas.height - renderedHeight);//可能内容不足一页

            //用getImageData剪裁指定区域，并画到前面创建的canvas对象中
            page.getContext('2d').putImageData(ctx.getImageData(0, renderedHeight, canvas.width, Math.min(imgHeight, canvas.height - renderedHeight)), 0, 0);
            pdf.addImage(page.toDataURL('image/jpeg', 0.8), 'JPEG', 10, 5, pw, Math.min(ph, pw * page.height / page.width));    //添加图像到页面，保留10mm边距

            img = page.toDataURL('image/jpeg', 1);
            renderedHeight += imgHeight;

            if(renderedHeight < canvas.height) {
              pdf.addPage();//如果后面还有内容，添加一个空页
            }
          }

          console.log(`第 ${index + 1} 個pdf`);

          if (index + 1 !== el.length) pdf.addPage();
        }

        // 導出圖片
        this.exportImg(img);

        resolve({ PDF: pdf });
      })
    },
    // pdf 事件處理
    pdfEventHandler (promises) {
      Promise.all(promises).then(async (pdfs) => {
        const zip = new JSZip();

        promises.forEach(async (item, index) => {
          const { PDF } = item;

          // 預覽PDF
          if (this.isIframePreviewPdf) this.iframePreviewPdf(PDF);

          if (promises.length === 1) {
            // 下載PDF
            if (this.enableDownload) PDF.save(this.filename + '.pdf');
          } else {
            await zip.file(`${this.filename}_${index + 1}.pdf`, PDF.output('blob'));
          }
        });

        if (promises.length > 1) {
          zip.generateAsync({ type: 'blob' }).then(content => {
            FileSaver.saveAs(content, `${this.filename}.zip`);
          });
        }

        console.log(`結束時間：${this.getNowTime()}`);

        this.isLoading = false;
        this.$emit('handlerCompleted');
      });
    },
    getNowTime() {
      const date = new Date(),
        minute = String(date.getMinutes()).padStart(2, '0'),
        sec = String(date.getSeconds()).padStart(2, '0'),
        hours = String(date.getHours()).padStart(2, '0');

      return `${hours}:${minute}:${sec}`;
    },
    // 導出圖片
    exportImg(url) {
      this.$emit('exportImg', url)
    },
    // 使用Iframe預覽PDF
    iframePreviewPdf(pdf) {
     // 生成PDF文件
      const pdfBlob = pdf.output('blob');

      // 创建一个新的Blob URL用于在iframe中打开PDF预览
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // 创建一个iframe元素来显示PDF预览
      const iframe = document.createElement('iframe');
      iframe.title = '新標題';
      iframe.src = pdfUrl;
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = 'none';

      // 将iframe添加到DOM中
      const previewWindow = window.open();
      previewWindow.document.body.appendChild(iframe);
    },
    async zipPdf () {
      // 方法一(用for不能用forEach)
      this.isLoading = true;

      const date = new Date(),
        hours = date.getHours(),
        minute = date.getMinutes(),
        sec = date.getSeconds();

      // console.log(hours + ':' + minute + ':' + sec);

      const parentElement = this.$refs.pdfContent;
      const ArrOfContentChildren = Array.from(parentElement.children);
      const childElementCount = parentElement.childElementCount;
      const num = this.zipSplitPdfNum;
      const batch = childElementCount < num ? 1 : Math.ceil(childElementCount / num);

      let promises = [];

      for (let i = 0 ; i < batch; i++) {
        const slice_end =  childElementCount < num ? childElementCount + 1 : i * num + num;
        const el = ArrOfContentChildren.slice(i * num, slice_end);
        const p = await this.getPdf3(el, `${sec}_${i}`);
        console.log('out' + i);
        promises.push(p);

        if (i + 1 === batch) {
          this.zipChange(promises);
        }
      }
    },
    getPdf3(el) {
      return new Promise(async(resolve, reject) => {
        const params = {
          allowTaint: false,
          taintTest: false,
          logging: false,
          useCORS: false,
          dpi: window.devicePixelRatio * 2, //将分辨率提高到特定的DPI 提高四倍
          scale: 2 //按比例增加分辨率
        };
        const pdf = new jsPDF('p', 'mm', 'a4');    //A4纸，纵向

        for (const [index, childElement] of el.entries()) {
          const canvas = await html2Canvas(childElement, params);

          let ctx = canvas.getContext('2d'),
              a4w = 190, a4h = 287,    //A4大小，210mm x 297mm，四边各保留10mm的边距，显示区域190x277
              imgHeight = Math.floor(a4h * canvas.width / a4w),    //按A4显示比例换算一页图像的像素高度
              renderedHeight = 0;

          while (renderedHeight < canvas.height) {
            const page = document.createElement("canvas");
            page.width = canvas.width;
            page.height = Math.min(imgHeight, canvas.height - renderedHeight);//可能内容不足一页

            //用getImageData剪裁指定区域，并画到前面创建的canvas对象中
            page.getContext('2d').putImageData(ctx.getImageData(0, renderedHeight, canvas.width, Math.min(imgHeight, canvas.height - renderedHeight)), 0, 0);
            pdf.addImage(page.toDataURL('image/jpeg', 0.8), 'JPEG', 10, 5, a4w, Math.min(a4h, a4w * page.height / page.width));    //添加图像到页面，保留10mm边距

            img = page.toDataURL('image/jpeg', 1);
            renderedHeight += imgHeight;

            if (renderedHeight < canvas.height) {
              pdf.addPage(); //如果后面还有内容，添加一个空页
            }
          }

          if (index + 1 !== el.length) pdf.addPage();
        }

        resolve({ PDF: pdf });
      });
    },
    zipChange (promises) {
      Promise.all(promises).then(async (pdfs) => {
        const zip = new JSZip();
        const date = new Date(),
            minute = date.getMinutes(),
            sec = date.getSeconds(),
            hours = date.getHours();


        promises.forEach(async (item, index) => {
          const { PDF } = item;

          if (promises.length === 1) {

            PDF.save(`${this.filename}.pdf`);
          } else {
            await zip.file(`${this.filename}_${index + 1}.pdf`, PDF.output('blob'));
          }
        });

        if (promises.length > 1) {
          zip.generateAsync({ type: 'blob' }).then(content => {
            FileSaver.saveAs(content, `${hours}${minute}${sec}` + '.zip');
          });
        }

        console.log(hours + ':' + minute + ':' + sec);

        this.isLoading = false;
        this.$emit('handlerCompleted');
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.hide-wrapper {
  position: fixed;
  z-index: -9999;
}
</style>



// WEBPACK FOOTER //
// src/components/common/HtmlToPdf.vue